import { getReferenceExample, getInTextExample } from '../../../config/citation-config-new';
import { generateReference, generateInText } from '../../../utils/citationGenerator';

export default function CitationPreview({ selectedCategory, selectedSource, citationData }) {
	// Inline style for better word breaking of long URLs
	const textBreakStyle = {
		wordBreak: 'break-word',
		overflowWrap: 'anywhere'
	};
	// Get static examples
	const referenceExample =
		selectedCategory && selectedSource ? getReferenceExample(selectedCategory, selectedSource) : '';

	const inTextExample =
		selectedCategory && selectedSource ? getInTextExample(selectedCategory, selectedSource) : '';

	// Generate live citations from user input
	const liveReference =
		selectedCategory && selectedSource && citationData
			? generateReference(selectedCategory, selectedSource, citationData)
			: '';

	const liveInText =
		selectedCategory && selectedSource && citationData
			? generateInText(selectedCategory, selectedSource, citationData)
			: { parenthetical: '', narrative: '' };

	// Parse static in-text examples
	const parseInTextExamples = (exampleText) => {
		if (!exampleText) return { parenthetical: '', narrative: '' };

		const lines = exampleText.split('\n');
		let parenthetical = '';
		let narrative = '';

		lines.forEach((line) => {
			if (line.includes('Parenthetical:')) {
				parenthetical = line.replace('Parenthetical:', '').trim();
			} else if (line.includes('Narrative:')) {
				narrative = line.replace('Narrative:', '').trim();
			}
		});

		return { parenthetical, narrative };
	};

	// Generate generic in-text format templates
	const getInTextFormatTemplate = () => {
		// Generic format that applies to most citation types
		return {
			parenthetical: '(Author, Year)',
			narrative: 'Author (Year)'
		};
	};

	// Parse reference example to separate format template from example
	const parseReferenceExample = (exampleText) => {
		if (!exampleText) return { format: '', example: '' };

		// Split by double newline - format comes first, then example
		const parts = exampleText.split('\n\n');

		if (parts.length >= 2) {
			return {
				format: parts[0].trim(),
				example: parts.slice(1).join('\n\n').trim() // Join remaining parts in case there are multiple examples
			};
		}

		// If no double newline, treat the whole thing as an example
		return { format: '', example: exampleText.trim() };
	};

	const staticInTextParsed = parseInTextExamples(inTextExample);
	const staticReferenceParsed = parseReferenceExample(referenceExample);

	// Determine if we should show live preview or static example
	const hasUserData =
		citationData &&
		Object.keys(citationData).length > 0 &&
		Object.values(citationData).some((val) => {
			if (Array.isArray(val)) return val.some((v) => v && v.trim());
			return val && val.toString().trim();
		});

	if (!selectedCategory || !selectedSource) {
		return (
			<div className="bg-gray-50 rounded-[20px] p-6">
				<div className="mb-4">
					<h3 className="text-[25px] font-bold text-dark-blue mb-2">Live Preview</h3>
					<p className="text-sm text-gray">
						Select a category and source type to preview your formatted citation.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="bg-gray-50 rounded-[20px] p-6 pr-2">
			<div className="mb-4">
				<h3 className="text-[25px] font-bold text-dark-blue mb-2">Live Preview</h3>
				<p className="text-sm text-gray">
					{hasUserData
						? 'Your citation updates as you type'
						: 'Here is a quick APA citation preview'}
				</p>
			</div>

			{/* In-text citations */}
			<div className="mb-6">
				<h4 className="text-sm font-medium text-dark-blue mb-2">
					In-text {hasUserData ? '' : 'Examples'}
				</h4>

				{hasUserData ? (
					// Show live in-text citations when user has entered data
					<div className="space-y-2">
						{liveInText.parenthetical && (
							<div className="flex pl-[10px] items-start gap-[10px] self-stretch border-l-[1.5px] border-[#E8E8EA] pr-2">
								<p className="text-sm text-dark-blue/60" style={textBreakStyle}>
									<span className="font-medium">Parenthetical:</span> {liveInText.parenthetical}
								</p>
							</div>
						)}
						{liveInText.narrative && (
							<div className="flex pl-[10px] items-start gap-[10px] self-stretch border-l-[1.5px] border-[#E8E8EA] pr-2">
								<p className="text-sm text-dark-blue/60" style={textBreakStyle}>
									<span className="font-medium">Narrative:</span> {liveInText.narrative}
								</p>
							</div>
						)}
					</div>
				) : (
					// Show format template and examples when no user data
					<div className="space-y-4">
						{/* Parenthetical - Format and Example */}
						{staticInTextParsed.parenthetical && (
							<div>
								<div className="flex pl-[10px] items-start gap-[10px] self-stretch border-l-[1.5px] border-[#E8E8EA] mb-2 pr-2">
									<p className="text-sm text-dark-blue/60" style={textBreakStyle}>
										<span className="font-medium">Parenthetical:</span>{' '}
										{getInTextFormatTemplate().parenthetical}
									</p>
								</div>
								<div className="flex pl-[10px] items-start gap-[10px] self-stretch border-l-[1.5px] border-[#E8E8EA] mb-1 pr-2">
									<p className="text-sm text-dark-blue/60" style={textBreakStyle}>
										{staticInTextParsed.parenthetical}
									</p>
								</div>
								<p className="text-xs text-dark-blue/50 pl-[10px]">
									Use parenthetical citations at the end of a sentence.
								</p>
							</div>
						)}

						{/* Narrative - Format and Example */}
						{staticInTextParsed.narrative && (
							<div>
								<div className="flex pl-[10px] items-start gap-[10px] self-stretch border-l-[1.5px] border-[#E8E8EA] mb-2 pr-2">
									<p className="text-sm text-dark-blue/60" style={textBreakStyle}>
										<span className="font-medium">Narrative:</span>{' '}
										{getInTextFormatTemplate().narrative}
									</p>
								</div>
								<div className="flex pl-[10px] items-start gap-[10px] self-stretch border-l-[1.5px] border-[#E8E8EA] mb-1 pr-2">
									<p className="text-sm text-dark-blue/60" style={textBreakStyle}>
										{staticInTextParsed.narrative}
									</p>
								</div>
								<p className="text-xs text-dark-blue/50 pl-[10px]">
									Use narrative citations when the author is part of the sentence.
								</p>
							</div>
						)}
					</div>
				)}
			</div>

			{/* Reference */}
			<div className="my-6">
				<h4 className="text-sm font-medium text-dark-blue mb-2">
					Reference entry {hasUserData ? '' : 'Example'}
				</h4>

				{hasUserData && liveReference ? (
					// Show live reference when user has entered data
					<div className="flex pl-[10px] items-start gap-[10px] self-stretch border-l-[1.5px] border-[#E8E8EA] pr-2">
						<p
							className="text-sm text-dark-blue/60 leading-relaxed whitespace-pre-line"
							style={textBreakStyle}
							dangerouslySetInnerHTML={{
								__html: liveReference
							}}
						/>
					</div>
				) : (
					// Show format template and example when no user data
					<div className="space-y-3">
						{/* Format Template */}
						{staticReferenceParsed.format && (
							<div className="flex pl-[10px] items-start gap-[10px] self-stretch border-l-[1.5px] border-[#E8E8EA] pr-2">
								<p
									className="text-sm text-dark-blue/60 leading-relaxed"
									style={textBreakStyle}
									dangerouslySetInnerHTML={{
										__html: staticReferenceParsed.format
									}}
								/>
							</div>
						)}

						{/* Example */}
						{staticReferenceParsed.example && (
							<div className="flex pl-[10px] items-start gap-[10px] self-stretch border-l-[1.5px] border-[#E8E8EA] pr-2">
								<p
									className="text-sm text-dark-blue/60 leading-relaxed whitespace-pre-line"
									style={textBreakStyle}
									dangerouslySetInnerHTML={{
										__html: staticReferenceParsed.example
									}}
								/>
							</div>
						)}

						{!staticReferenceParsed.format && !staticReferenceParsed.example && (
							<div className="flex pl-[10px] items-start gap-[10px] self-stretch border-l-[1.5px] border-[#E8E8EA] pr-2">
								<p className="text-sm text-dark-blue/60 leading-relaxed" style={textBreakStyle}>
									Fill in the fields to see your citation...
								</p>
							</div>
						)}
					</div>
				)}
			</div>

			{/* Additional notes */}
			{!hasUserData && (
				<div className="mt-6 p-3 bg-blue-50 rounded-lg border border-blue-100">
					<p className="text-xs text-dark-blue/70">
						<span className="font-semibold">Note:</span> Start entering source details to see your APA citation update in real time.
					</p>
				</div>
			)}

			{hasUserData && (
				<div className="mt-6 p-3 bg-green-50 rounded-lg border border-green-100">
					<p className="text-xs text-dark-blue/70">
						<span className="font-semibold">✓ Live preview active.</span> Your citation updates as
						you type. Complete all required fields to finish.
					</p>
				</div>
			)}
		</div>
	);
}
